"use client";

import { Label } from "@/components/ui/label";
import { useInteractions } from "../../../hooks/interactionsProvider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PlusCircle, Trash } from "lucide-react";
import { MatchInteractionContent as MatchInteractionContentType } from "@yedalabs/schema";
import { v4 as uuid } from "uuid";
import { useMemo } from "react";
import { t } from "@/lib/localization";

export default function MatchInteractionContent() {
    const { selectedInteraction, updateInteraction } = useInteractions();

    const pairs = useMemo(() => {
        if (selectedInteraction?.type === "match") {
            return selectedInteraction?.content.pairs || [];
        } else {
            return [];
        }
    }, [selectedInteraction]);

    if (!selectedInteraction || selectedInteraction.type !== "match")
        return <></>;

    const updatePair = (
        id: string,
        fieldsToUpdate: Omit<
            Partial<MatchInteractionContentType["pairs"][0]>,
            "id"
        >
    ) => {
        updateInteraction(selectedInteraction.id, {
            content: {
                ...selectedInteraction.content,
                pairs: pairs.map((pair) =>
                    pair.id === id ? { ...pair, ...fieldsToUpdate } : pair
                ),
            },
        });
    };

    const addPair = () => {
        updateInteraction(selectedInteraction.id, {
            content: {
                ...selectedInteraction.content,
                pairs: [
                    ...pairs,
                    {
                        id: uuid(),
                        item1: "",
                        item2: "",
                    },
                ],
            },
        });
    };

    const deletePair = (id: string) => {
        updateInteraction(selectedInteraction.id, {
            content: {
                ...selectedInteraction.content,
                pairs: pairs.filter((pair) => pair.id !== id),
            },
        });
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-row justify-between items-center">
                <Label>
                    {t("interaction-modal.match.answers.label", undefined, undefined)}
                </Label>
                <Button
                    variant="link"
                    size="xs"
                    onClick={() => addPair()}
                    type="button"
                    className="flex flex-row gap-1.5 items-center text-black h-fit px-0"
                >
                    <PlusCircle className="size-3.5" />
                    {t("interaction-modal.answers.add-btn", undefined, undefined)}
                </Button>
            </div>

            <div className="flex flex-col gap-2">
                {pairs.map((pair, idx) => (
                    <div key={idx} className="flex flex-row gap-2 items-center">
                        <span className="w-8 text-sm text-muted-foreground">
                            {idx + 1}.
                        </span>
                        <Input
                            className="h-8 text-xs"
                            value={pair.item1}
                            onChange={(e) =>
                                updatePair(pair.id, {
                                    item1: e.target.value,
                                })
                            }
                        />
                        <Input
                            className="h-8 text-xs"
                            value={pair.item2}
                            onChange={(e) =>
                                updatePair(pair.id, {
                                    item2: e.target.value,
                                })
                            }
                        />
                        <button
                            onClick={() => deletePair(pair.id)}
                            type="button"
                        >
                            <Trash className="ms-2 text-destructive size-[16px]" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
